import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGameFlow } from '../hooks/useGameFlow';
import { useGameContext } from '../contexts/GameContext';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { JSONPersistenceService } from '../services/JSONPersistenceService';
import { scenarioRecommendationService, ScoredScenario } from '../services/ScenarioRecommendationService';
import { Scenario as ScenarioData } from '../types/ScenarioManagement';
import { RecommendedScenarioCard } from '../components/RecommendedScenarioCard';
import { Footer } from '../components/Footer';
import { logger } from '../services/LoggingService';
import { trackEmailGateEvent } from '../services/EmailGatingService';
import './ScenarioSelectionScreen.css';

interface Scenario {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: string;
  isAvailable: boolean;
  difficulty: string;
  categories?: string[];
  tags?: string[];
}

type RoleFilter = 'all' | 'tech' | 'exec';
type IndustryFilter = 'all' | 'healthcare' | 'fintech' | 'technology';

const ScenarioSelectionScreen: React.FC = () => {
  const navigate = useNavigate();
  const { navigateToCEO } = useGameFlow();
  const { setSelectedScenario } = useGameContext();
  const { preferences, hasPreferences, resetPreferences } = useUserPreferences();

  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [fullScenarioData, setFullScenarioData] = useState<Map<string, ScenarioData>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAllScenarios, setShowAllScenarios] = useState(false);

  // Initialize filters based on user preferences
  const [roleFilter, setRoleFilter] = useState<RoleFilter>(() =>
    preferences.role || 'all'
  );
  const [industryFilter, setIndustryFilter] = useState<IndustryFilter>(() =>
    preferences.industry === 'other' ? 'all' : (preferences.industry || 'all')
  );

  useEffect(() => {
    loadScenarios();
  }, []);

  const loadScenarios = async () => {
    try {
      setLoading(true);
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();

      if (data && data.scenarios) {
        // Store full scenario data
        const scenarioMap = new Map<string, ScenarioData>();
        data.scenarios.forEach(([id, scenario]) => {
          if (!scenario.deletedAt) {
            scenarioMap.set(id, scenario);
          }
        });
        setFullScenarioData(scenarioMap);

        // Convert from Map structure to array and filter out deleted scenarios
        const scenarioArray = data.scenarios
          .filter(([id, scenario]) => !scenario.deletedAt)
          .map(([id, scenario]) => {
            const rawScenario = scenario as ScenarioData & { categories?: string[]; tags?: string[] };
            return {
              id: scenario.id,
              title: scenario.title,
              subtitle: scenario.subtitle,
              description: scenario.description,
              icon: scenario.icon,
              isAvailable: scenario.isAvailable,
              difficulty: scenario.difficulty,
              categories: rawScenario.categories,
              tags: rawScenario.tags,
            };
          });

        setScenarios(scenarioArray);
      } else {
        // Initialize with default data if none exists
        await persistence.initializeWithDefaults();
        const defaultData = await persistence.loadData();
        if (defaultData && defaultData.scenarios) {
          const scenarioMap = new Map<string, ScenarioData>();
          defaultData.scenarios.forEach(([id, scenario]) => {
            if (!scenario.deletedAt) {
              scenarioMap.set(id, scenario);
            }
          });
          setFullScenarioData(scenarioMap);

          const scenarioArray = defaultData.scenarios
            .filter(([id, scenario]) => !scenario.deletedAt)
            .map(([id, scenario]) => {
              const rawScenario = scenario as ScenarioData & { categories?: string[]; tags?: string[] };
              return {
                id: scenario.id,
                title: scenario.title,
                subtitle: scenario.subtitle,
                description: scenario.description,
                icon: scenario.icon,
                isAvailable: scenario.isAvailable,
                difficulty: scenario.difficulty,
                categories: rawScenario.categories,
                tags: rawScenario.tags,
              };
            });
          setScenarios(scenarioArray);
        }
      }
    } catch (err) {
      logger.error('Failed to load scenarios', 'ScenarioSelectionScreen', {}, err as Error);
      setError('Failed to load scenarios. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleScenarioSelect = (scenario: Scenario) => {
    try {
      if (!scenario || !scenario.isAvailable) return;

      const fullScenario = fullScenarioData.get(scenario.id);
      if (fullScenario) {
        // Track scenario selection
        trackEmailGateEvent('scenario_selected', {
          scenarioId: scenario.id,
          scenarioTitle: scenario.title,
          role: preferences.role,
          industry: preferences.industry,
        });
        // Track exercise start
        trackEmailGateEvent('exercise_start', {
          scenarioId: scenario.id,
          scenarioTitle: scenario.title,
          role: preferences.role,
          industry: preferences.industry,
        });
        setSelectedScenario(fullScenario);
        navigateToCEO();
      } else {
        logger.error('Full scenario data not found', 'ScenarioSelectionScreen', {
          scenarioId: scenario.id
        });
      }
    } catch (error) {
      logger.error('Error selecting scenario', 'ScenarioSelectionScreen', {
        scenarioId: scenario.id
      }, error as Error);
    }
  };

  const handleRecommendedSelect = (scenarioId: string) => {
    const scenario = scenarios.find(s => s.id === scenarioId);
    if (scenario) {
      handleScenarioSelect(scenario);
    }
  };

  const handleChangePreferences = () => {
    resetPreferences();
    navigate('/');
  };

  const getDifficultyColor = (difficulty: string | undefined): string => {
    if (!difficulty) return 'text-slate-400';
    switch (difficulty.toLowerCase()) {
      case 'beginner': return 'text-emerald-400';
      case 'intermediate': return 'text-yellow-400';
      case 'advanced': return 'text-orange-400';
      case 'expert': return 'text-red-400';
      case 'tech': return 'text-blue-400';
      case 'exec': return 'text-purple-400';
      default: return 'text-slate-400';
    }
  };

  const getIndustry = (scenario: Scenario): 'healthcare' | 'fintech' | 'technology' | 'other' => {
    const allTags = [...(scenario.categories || []), ...(scenario.tags || [])].map(t => t.toLowerCase());

    if (allTags.includes('healthcare') || allTags.includes('hipaa')) return 'healthcare';
    if (allTags.includes('fintech') || allTags.includes('financial') || allTags.includes('payment')) return 'fintech';
    if (allTags.includes('technology') || allTags.includes('saas') || allTags.includes('software')) return 'technology';

    const title = scenario.title.toLowerCase();
    if (title.includes('health')) return 'healthcare';
    if (title.includes('financial') || title.includes('fintech')) return 'fintech';
    if (title.includes('tech')) return 'technology';

    return 'other';
  };

  // Get recommendations based on user preferences
  const recommendations: ScoredScenario[] = useMemo(() => {
    if (!hasPreferences || scenarios.length === 0) return [];
    return scenarioRecommendationService.getRecommendations(
      scenarios,
      preferences.role,
      preferences.industry,
      2
    );
  }, [scenarios, preferences.role, preferences.industry, hasPreferences]);

  // Get IDs of recommended scenarios to exclude from main grid
  const recommendedIds = useMemo(() =>
    new Set(recommendations.map(r => r.scenario.id)),
    [recommendations]
  );

  const filteredScenarios = scenarios.filter(scenario => {
    // If showing recommendations, exclude them from main grid
    if (recommendations.length > 0 && !showAllScenarios && recommendedIds.has(scenario.id)) {
      return false;
    }

    // Role filter
    if (roleFilter !== 'all') {
      const difficulty = (scenario.difficulty || '').toLowerCase();
      if (roleFilter === 'tech' && difficulty !== 'tech') return false;
      if (roleFilter === 'exec' && difficulty !== 'exec') return false;
    }

    // Industry filter
    if (industryFilter !== 'all') {
      const industry = getIndustry(scenario);
      if (industry !== industryFilter) return false;
    }

    return true;
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const tileVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring" as const,
        stiffness: 100,
        damping: 15,
      },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-dark-primary dark:to-dark-secondary py-8 px-4 transition-all duration-500">
      <motion.div
        className="max-w-7xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div className="text-center mb-8" variants={tileVariants}>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-wide mb-4 bg-gradient-to-r from-slate-900 to-slate-600 dark:from-aurora-violet-300 dark:via-aurora-cyan-300 dark:to-aurora-magenta-300 bg-clip-text text-transparent">
            {hasPreferences ? 'Your Scenarios' : 'Cybersecurity Risk Simulation'}
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {hasPreferences
              ? 'Based on your preferences, here are scenarios tailored for you.'
              : 'Choose your executive scenario and test your ability to navigate complex cybersecurity challenges in high-stakes business environments.'
            }
          </p>
          {hasPreferences && (
            <button
              onClick={handleChangePreferences}
              className="mt-3 text-sm text-aurora-violet-600 dark:text-aurora-violet-400 hover:underline"
            >
              Change preferences
            </button>
          )}
        </motion.div>

        {/* Recommendations Section */}
        {recommendations.length > 0 && !showAllScenarios && (
          <motion.div className="mb-10" variants={tileVariants}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                <svg className="w-6 h-6 text-aurora-violet-500" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                Recommended for You
              </h2>
              <button
                onClick={() => setShowAllScenarios(true)}
                className="text-sm text-aurora-violet-600 dark:text-aurora-violet-400 hover:underline flex items-center gap-1"
              >
                See all {scenarios.length} scenarios
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {recommendations.map((rec) => (
                <RecommendedScenarioCard
                  key={rec.scenario.id}
                  id={rec.scenario.id}
                  title={rec.scenario.title}
                  subtitle={(rec.scenario as Scenario).subtitle}
                  description={(rec.scenario as Scenario).description}
                  icon={(rec.scenario as Scenario).icon}
                  difficulty={rec.scenario.difficulty}
                  isAvailable={rec.scenario.isAvailable}
                  matchReasons={rec.matchReasons}
                  onSelect={() => handleRecommendedSelect(rec.scenario.id)}
                />
              ))}
            </div>
          </motion.div>
        )}

        {/* Filters - show when viewing all scenarios or no recommendations */}
        {(showAllScenarios || recommendations.length === 0) && (
          <motion.div
            className="flex flex-wrap justify-center gap-6 mb-8"
            variants={tileVariants}
          >
            {/* Role Filter */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Role:</span>
              <div className="flex bg-white/80 dark:bg-dark-elevated/50 rounded-lg p-1 border border-slate-200 dark:border-aurora-violet-500/20">
                <button
                  onClick={() => setRoleFilter('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    roleFilter === 'all'
                      ? 'bg-professional-blue-600 dark:bg-aurora-violet-600 text-white shadow-md dark:shadow-aurora-violet'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-aurora-violet-500/10'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setRoleFilter('tech')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    roleFilter === 'tech'
                      ? 'bg-blue-500 text-white shadow-md'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
                  }`}
                >
                  Tech
                </button>
                <button
                  onClick={() => setRoleFilter('exec')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    roleFilter === 'exec'
                      ? 'bg-purple-500 text-white shadow-md'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
                  }`}
                >
                  Exec
                </button>
              </div>
            </div>

            {/* Industry Filter */}
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Industry:</span>
              <div className="flex bg-white/80 dark:bg-dark-elevated/50 rounded-lg p-1 border border-slate-200 dark:border-aurora-violet-500/20">
                <button
                  onClick={() => setIndustryFilter('all')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    industryFilter === 'all'
                      ? 'bg-professional-blue-600 dark:bg-aurora-violet-600 text-white shadow-md dark:shadow-aurora-violet'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-aurora-violet-500/10'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setIndustryFilter('healthcare')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    industryFilter === 'healthcare'
                      ? 'bg-teal-500 text-white shadow-md'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
                  }`}
                >
                  Healthcare
                </button>
                <button
                  onClick={() => setIndustryFilter('fintech')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    industryFilter === 'fintech'
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
                  }`}
                >
                  FinTech
                </button>
                <button
                  onClick={() => setIndustryFilter('technology')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                    industryFilter === 'technology'
                      ? 'bg-indigo-500 text-white shadow-md'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/10'
                  }`}
                >
                  Technology
                </button>
              </div>
            </div>

            {/* Back to recommendations */}
            {showAllScenarios && recommendations.length > 0 && (
              <button
                onClick={() => setShowAllScenarios(false)}
                className="text-sm text-aurora-violet-600 dark:text-aurora-violet-400 hover:underline flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to recommendations
              </button>
            )}
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-professional-blue-600 dark:border-aurora-violet-500"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={loadScenarios}
              className="px-6 py-3 bg-professional-blue-600 text-white rounded-lg hover:bg-professional-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Scenario Grid - All other scenarios */}
        {!loading && !error && (showAllScenarios || recommendations.length === 0 || filteredScenarios.length > 0) && (
          <>
            {/* Section header when showing recommendations */}
            {recommendations.length > 0 && !showAllScenarios && filteredScenarios.length > 0 && (
              <motion.h2
                className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white mb-4"
                variants={tileVariants}
              >
                More Scenarios
              </motion.h2>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {filteredScenarios.length === 0 && (showAllScenarios || recommendations.length === 0) && (
                <div className="col-span-full text-center py-12">
                  <p className="text-slate-500 dark:text-slate-400 text-lg">
                    No scenarios match the selected filters.
                  </p>
                  <button
                    onClick={() => { setRoleFilter('all'); setIndustryFilter('all'); }}
                    className="mt-4 text-professional-blue-600 dark:text-professional-blue-400 hover:underline"
                  >
                    Clear filters
                  </button>
                </div>
              )}
              {filteredScenarios.map((scenario) => (
                <div
                  key={scenario.id}
                  className={`scenario-tile ${
                    scenario.isAvailable
                      ? 'cursor-pointer hover:scale-[1.02] hover:-translate-y-1 hover:shadow-2xl'
                      : 'cursor-not-allowed'
                  } transition-all duration-300`}
                  onClick={() => handleScenarioSelect(scenario)}
                >
                  <div className="bg-white/90 dark:bg-gradient-to-br dark:from-dark-secondary dark:to-dark-accent border border-slate-200 dark:border-aurora-violet-500/30 rounded-2xl p-6 sm:p-8 backdrop-blur-md shadow-xl dark:shadow-aurora-card h-full flex flex-col transition-all duration-300 dark:hover:border-aurora-violet-500/50 dark:hover:shadow-aurora-violet">
                    {/* Icon and Status */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-4xl sm:text-5xl">{scenario.icon || '🎯'}</div>
                      <div className="flex flex-col items-end gap-2">
                        {!scenario.isAvailable && (
                          <span className="bg-slate-200 text-slate-600 dark:bg-slate-500/20 dark:text-slate-400 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider">
                            Coming Soon
                          </span>
                        )}
                        {scenario.difficulty && (
                          <span className={`text-sm font-medium ${getDifficultyColor(scenario.difficulty)}`}>
                            {scenario.difficulty}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Title and Subtitle */}
                    <div className="mb-4">
                      <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 dark:text-white mb-2">
                        {scenario.title || 'Untitled Scenario'}
                      </h2>
                      {scenario.subtitle && (
                        <h3 className="text-base sm:text-lg text-professional-blue-600 dark:text-aurora-cyan-400 font-medium">
                          {scenario.subtitle}
                        </h3>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-slate-700 dark:text-slate-300 leading-relaxed flex-1 mb-6">
                      {scenario.description || 'No description available'}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        {/* Removed duration display */}
                      </div>

                      {scenario.isAvailable ? (
                        <button className="bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 dark:from-aurora-violet-600 dark:to-aurora-violet-700 text-white px-6 py-2 rounded-lg font-medium hover:shadow-lg dark:hover:shadow-aurora-violet transition-all duration-200 hover:-translate-y-0.5">
                          Start Simulation
                        </button>
                      ) : (
                        <button disabled className="bg-slate-300 text-slate-500 dark:bg-slate-600/50 dark:text-slate-400 px-6 py-2 rounded-lg font-medium cursor-not-allowed">
                          Coming Soon
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Footer */}
        <motion.div variants={tileVariants}>
          <Footer className="mt-8" />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ScenarioSelectionScreen;
